import React from 'react';
import { Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'hooks/redux';
import { selectPads } from 'selectors/liveAircraftSelectors';
import { updateDeparturePadThunk, updateArrivalPadThunk } from 'actions/orderActions';
import { rerouteOrderThunk } from 'actions/adminActions';
import { Order } from '@m4a3/api-client';
import { getShortIdForOrder } from '../../../utils/orderUtils';
import { logToDatadog } from '../../../services/datadog';
import { getAllActivePadsForOrder } from '../../../utils/landingPadUtils';

interface PadSelectorProps {
  order: Order;
  onClose: () => void;
  onError: (message: string) => void;
}

const PadSelector = ({ order, onClose, onError }: PadSelectorProps) => {
  const dispatch = useDispatch();
  const pads = useAppSelector(selectPads);
  const shortOrderId = getShortIdForOrder(order);

  // Use shared utility function for pad filtering logic
  const getAvailablePads = () => getAllActivePadsForOrder(order, pads || []);

  const handlePadSelection = async (padId: string | null) => {
    if (!order) return;
    
    try {
      // Update both departure and arrival pads (same logic as OrderCardContext)
      await dispatch(updateDeparturePadThunk(order.id, padId));
      await dispatch(updateArrivalPadThunk(order.id, padId));
      onClose();
      await dispatch(rerouteOrderThunk(order.id));
    } catch (err) {
      logToDatadog('Error setting pads from OrderDetails', {err});
      onError('Failed to assign pads');
      onClose();
    }
  };

  return (
    <div style={{
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      backgroundColor: 'white',
      marginBottom: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #F1F5F9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" style={{ fontSize: 16, fontWeight: 600 }}>
          #{shortOrderId} - Assign Departure Pad
        </Typography>
        <div 
          onClick={onClose}
          style={{ cursor: 'pointer', padding: '4px' }}
        >
          ←
        </div>
      </div>
      
      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
        <div
          onClick={() => handlePadSelection(null)}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: '1px solid #F1F5F9',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F8FAFC';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          None
        </div>
        
        {getAvailablePads().length > 0 ? (
          getAvailablePads().map(pad => (
            <div
              key={`pad-selector-${pad.id}`}
              onClick={() => handlePadSelection(pad.id.toString())}
              style={{
                padding: '12px 16px',
                cursor: pad.aircraft ? 'pointer' : 'not-allowed',
                borderBottom: '1px solid #F1F5F9',
                opacity: pad.aircraft ? 1 : 0.5,
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (pad.aircraft) {
                  e.currentTarget.style.backgroundColor = '#F8FAFC';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <div>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {pad.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
                  ({pad.aircraft || 'No aircraft assigned'})
                </Typography>
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: '12px 16px', color: 'gray' }}>
            No available pads in this cell
          </div>
        )}
      </div>
    </div>
  );
};

export default PadSelector;
